// bookie-midnight-preview — Midnight-blue paper invitation card.
//
// Mood: cool, after-hours, a bit cosmic. The outer frame is a deep
// midnight gradient with a faint silver shimmer; the inner card sits
// on the template's ivory paper background with a crescent-moon
// medallion centred above the names and a small-caps "TAKLIFNOMA"
// eyebrow on top. The category in Uzbek is "TAKLIFNOMA" (bookie).
import { type LayoutCtx } from "./_shared";

export function BookieMidnightPreviewLayout({
  names,
  date,
  venue,
  accent,
  ink,
  bg,
  font,
}: LayoutCtx) {
  // Deep midnight foil. Stops alternate dark / lighter periwinkle to
  // read as printed metallic at thumbnail scale.
  const FOIL =
    "linear-gradient(135deg, #0E1320 0%, #1A1F2E 28%, #2A3147 55%, #1A1F2E 82%, #0E1320 100%)";

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: FOIL,
        padding: "5%",
        display: "flex",
      }}
    >
      <div
        style={{
          flex: 1,
          background: bg,
          color: ink,
          padding: "8%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          boxShadow: `inset 0 0 0 1px ${accent}55`,
          position: "relative",
        }}
      >
        {/* Eyebrow */}
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 7,
            fontWeight: 700,
            letterSpacing: "0.4em",
            color: accent,
            marginBottom: "5%",
          }}
        >
          TAKLIFNOMA
        </div>

        {/* Crescent-moon medallion + tiny stars */}
        <div
          style={{
            width: "60%",
            aspectRatio: "1 / 1",
            position: "relative",
            marginBottom: "5%",
          }}
        >
          <svg
            viewBox="0 0 120 120"
            style={{ width: "100%", height: "100%", display: "block" }}
            aria-hidden
          >
            <defs>
              <radialGradient id="bkmn-moon" cx="0.32" cy="0.38" r="0.75">
                <stop offset="0%" stopColor={accent} stopOpacity="0.95" />
                <stop offset="100%" stopColor={accent} stopOpacity="0.3" />
              </radialGradient>
            </defs>
            {/* Outer hairline ring + inner hairline */}
            <circle cx="60" cy="60" r="56" fill="none" stroke={accent} strokeOpacity="0.45" strokeWidth="0.8" />
            <circle cx="60" cy="60" r="52" fill="none" stroke={accent} strokeOpacity="0.22" strokeWidth="0.4" />
            {/* Crescent moon */}
            <path
              d="M82 60 a26 26 0 1 1 -16 -24 a20 20 0 1 0 16 24 z"
              fill="url(#bkmn-moon)"
            />
            {/* Stars */}
            {[
              [24, 30], [98, 38], [30, 92], [96, 90], [60, 18], [60, 104],
              [44, 22], [82, 100],
            ].map(([cx, cy], i) => (
              <g key={i} transform={`translate(${cx} ${cy})`}>
                <path
                  d="M0 -2 L0.5 -0.5 L2 0 L0.5 0.5 L0 2 L-0.5 0.5 L-2 0 L-0.5 -0.5 Z"
                  fill={accent}
                  fillOpacity="0.7"
                />
              </g>
            ))}
          </svg>
        </div>

        {/* Names */}
        <div
          style={{
            fontFamily: font,
            fontSize: 30,
            lineHeight: 1.05,
            fontWeight: 400,
            color: ink,
          }}
        >
          {names}
        </div>

        <div style={{ flex: 1 }} />

        {/* Bottom caption */}
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6.5,
            fontWeight: 700,
            letterSpacing: "0.32em",
            color: accent,
            marginTop: "4%",
          }}
        >
          TAKLIFNOMA · {date}
        </div>
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6,
            fontWeight: 600,
            letterSpacing: "0.28em",
            color: `${ink}99`,
            marginTop: 6,
          }}
        >
          {venue.toUpperCase()}
        </div>
      </div>
    </div>
  );
}
